import type { NotificationEventMap } from './event-registry';
import { WEBHOOK_PAYLOAD_VERSION, WEBHOOK_USER_AGENT, type WebhookPayloadEnvelope } from './webhook.model';

export const WEBHOOK_EVENT_TYPES = [
  'calendar_slot_created',
  'calendar_slot_updated',
  'calendar_slot_deleted',
  'shift_time_override_by_employee',
  'shift_override_reset_by_planner',
  'planning.shift-override.approvalRequested',
  'planning.shift-override.decisionMade',
  'planning.shift-override.withdrawn',
  'timetracking.absence.approvalRequested',
  'timetracking.absence.decisionMade',
  'timetracking.absence.withdrawn',
  'timetracking.blocked_window_exception.approvalRequested',
  'timetracking.blocked_window_exception.decisionMade',
] as const;

export const WEBHOOK_TEST_EVENT_TYPE = 'webhook_test';

const SAMPLE_SLOT = {
  calendarId: '74669a2b-1102-497a-bd5d-2eb998b16990',
  slotId: 'b9f8a2f9-beac-47b9-8ddf-824a8b92db6d',
  dateFrom: '2026-11-02',
  dateTo: null,
  recurrenceRule: null,
  data: {
    type: 'external-booking',
    text: 'Consultation',
    startTime: '08:45',
    endTime: '09:00',
    isAllDay: false,
    customProperties: {
      customerName: 'Max Mustermann',
      emailAddress: 'max.mustermann@example.com',
      phoneNumber: '+41 79 000 00 00',
      meetingType: 'Video call',
    },
  },
};

const SAMPLE_TEAMS_LINK = { provider: 'teams', joinUrl: 'https://teams.microsoft.com/l/meetup-join/example' } as const;

const SAMPLE_REQUEST_ID = '5c2f8a10-9b3d-4e6a-a1c7-3d4e5f6a7b8c';
const SAMPLE_PERSON_ID = 'a4d2e7c0-1b3f-4c55-8e7a-9d6c2f1b0a33';

const SAMPLE_SHIFT_OVERRIDE = {
  requestId: SAMPLE_REQUEST_ID,
  planInstanceId: 'c1a1f0f4-5d1e-4f6c-8a52-0d0e5b3b7a10',
  date: '2026-11-02',
  shiftUnitName: 'Early shift',
  employeeName: 'Max Mustermann',
  requestedStartTime: '08:00',
  requestedEndTime: '16:00',
};

const WEBHOOK_EVENT_SAMPLES: Partial<{ [K in keyof NotificationEventMap]: NotificationEventMap[K] }> = {
  calendar_slot_created: { ...SAMPLE_SLOT, onlineMeeting: null },
  calendar_slot_updated: { ...SAMPLE_SLOT, onlineMeeting: SAMPLE_TEAMS_LINK },
  calendar_slot_deleted: { ...SAMPLE_SLOT, onlineMeeting: null },
  shift_time_override_by_employee: {
    planInstanceId: 'c1a1f0f4-5d1e-4f6c-8a52-0d0e5b3b7a10',
    shiftUnitId: '3b8d3c2a-7a64-4b8e-9f0b-2e6d1c4a9e55',
    shiftName: 'Early shift',
    date: '2026-11-02',
    employeeId: 'a4d2e7c0-1b3f-4c55-8e7a-9d6c2f1b0a33',
    employeeName: 'Max Mustermann',
    originalStart: '07:00',
    originalEnd: '15:00',
    newStart: '08:00',
    newEnd: '16:00',
  },
  shift_override_reset_by_planner: {
    planInstanceId: 'c1a1f0f4-5d1e-4f6c-8a52-0d0e5b3b7a10',
    shiftUnitId: '3b8d3c2a-7a64-4b8e-9f0b-2e6d1c4a9e55',
    shiftName: 'Early shift',
    date: '2026-11-02',
    plannerId: 'e9b0c1d2-3f4a-4b5c-8d6e-7f8091a2b3c4',
    plannerName: 'Petra Planner',
  },
  'planning.shift-override.approvalRequested': { ...SAMPLE_SHIFT_OVERRIDE },
  'planning.shift-override.decisionMade': { ...SAMPLE_SHIFT_OVERRIDE, decision: 'approved', comment: 'Fine by me' },
  'planning.shift-override.withdrawn': { ...SAMPLE_SHIFT_OVERRIDE },
  'timetracking.absence.approvalRequested': {
    requestId: SAMPLE_REQUEST_ID,
    personId: SAMPLE_PERSON_ID,
    personName: 'Max Mustermann',
    absenceTypeName: 'Vacation',
    fromDate: '2026-12-21',
    toDate: '2026-12-31',
    dayCount: 7,
  },
  'timetracking.absence.decisionMade': {
    requestId: SAMPLE_REQUEST_ID,
    decision: 'approved',
    absenceTypeName: 'Vacation',
    fromDate: '2026-12-21',
    toDate: '2026-12-31',
    decidedBy: 'Petra Planner',
    comment: 'Enjoy the break',
  },
  'timetracking.absence.withdrawn': {
    requestId: SAMPLE_REQUEST_ID,
    personName: 'Max Mustermann',
    absenceTypeName: 'Vacation',
    fromDate: '2026-12-21',
    toDate: '2026-12-31',
  },
  'timetracking.blocked_window_exception.approvalRequested': {
    requestId: SAMPLE_REQUEST_ID,
    personId: SAMPLE_PERSON_ID,
    personName: 'Max Mustermann',
    bookingId: '0f6b2c1e-8d3a-4a57-9c1b-5e7d2a4f8b90',
    date: '2026-11-02',
    windowKeys: ['night-rest'],
  },
  'timetracking.blocked_window_exception.decisionMade': {
    requestId: SAMPLE_REQUEST_ID,
    decision: 'rejected',
    bookingId: '0f6b2c1e-8d3a-4a57-9c1b-5e7d2a4f8b90',
    date: '2026-11-02',
    comment: null,
  },
};

export function webhookEventSample(eventType: string): Record<string, unknown> {
  const sample: unknown = WEBHOOK_EVENT_SAMPLES[eventType as keyof NotificationEventMap];
  return (sample as Record<string, unknown> | undefined) ?? {};
}

export function buildSystemWebhookEnvelope(id: string, event: string, payload: Record<string, unknown>): WebhookPayloadEnvelope {
  return {
    id,
    event,
    timestamp: new Date().toISOString(),
    version: WEBHOOK_PAYLOAD_VERSION,
    data: { notificationId: null, title: null, message: null, category: null, priority: null, metadata: null, payload },
  };
}

const FORBIDDEN_CUSTOM_HEADERS = new Set([
  'authorization',
  'x-webhook-signature-256',
  'host',
  'content-type',
  'content-length',
  'transfer-encoding',
]);

export interface WebhookRequestHeadersInput {
  signature: string;
  timestamp: string;
  webhookId: string;
  customHeaders: unknown;
  test?: boolean;
}

export function buildWebhookRequestHeaders({ signature, timestamp, webhookId, customHeaders, test }: WebhookRequestHeadersInput) {
  const custom =
    customHeaders && typeof customHeaders === 'object' && !Array.isArray(customHeaders) ? (customHeaders as Record<string, string>) : {};
  const allowedCustom = Object.fromEntries(Object.entries(custom).filter(([key]) => !FORBIDDEN_CUSTOM_HEADERS.has(key.toLowerCase())));
  return {
    'Content-Type': 'application/json',
    'User-Agent': WEBHOOK_USER_AGENT,
    'X-Webhook-Signature-256': signature,
    'X-Webhook-Id': webhookId,
    'X-Webhook-Timestamp': timestamp,
    ...(test ? { 'X-Webhook-Test': 'true' } : {}),
    ...allowedCustom,
  } as Record<string, string>;
}
